import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Header from "../components/Header";
import Footer from "../components/Footer";
import Hero from "../components/Hero";
import Features from "../components/Features";

const Home = () => {
  const [scrollY, setScrollY] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  // Redirect authenticated users to dashboard
  useEffect(() => {
    if (!loading && user) {
      navigate('/host/dashboard', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading]); // Removed navigate from dependencies to prevent infinite loop

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 100,
        y: (e.clientY / window.innerHeight) * 100
      });
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  // Show loading state while checking auth
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-emerald-50/20 via-white to-green-50/10">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-emerald-600 border-t-transparent"></div>
      </div>
    );
  }

  // Don't render landing page if user is authenticated
  if (user) {
    return null;
  }



  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-emerald-50 via-white to-stone-50 min-h-screen">
      {/* Serene animated background */}
      <div className="absolute inset-0 overflow-hidden">
        <div 
          className="absolute w-72 sm:w-[500px] h-72 sm:h-[500px] rounded-full bg-gradient-to-r from-emerald-50/30 to-green-50/20 blur-3xl"
          style={{
            left: `${15 + mousePosition.x * 0.02}%`,
            top: `${5 + mousePosition.y * 0.02}%`,
            transform: `translateY(${scrollY * 0.03}px)`
          }}
        />
        <div 
          className="absolute w-64 sm:w-96 h-64 sm:h-96 rounded-full bg-gradient-to-r from-stone-50/20 to-amber-50/15 blur-3xl"
          style={{
            right: `${10 + mousePosition.x * 0.02}%`,
            bottom: `${15 + mousePosition.y * 0.02}%`
          }}
        />
      </div>

      <Header />

      {/* Hero Section */}
      <main id="home" className="relative z-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          
          {/* Hero Content */}
          <Hero scrollY={scrollY} mousePosition={mousePosition} />

          {/* Features That Feel Like Hanging Out */}
          <Features />
        </div>
      </main>

      {/* Bottom CTA */}
      <section id="cta" className="scroll-mt-20 relative z-10 py-16 sm:py-20 lg:py-24 bg-gradient-to-br from-emerald-50 via-white to-stone-50 border-t border-emerald-100">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light text-gray-900 mb-4 sm:mb-6">
            Ready to Start Hosting?
          </h2>
          <p className="text-lg sm:text-xl lg:text-2xl text-gray-600 mb-8 sm:mb-10 lg:mb-12 font-light leading-relaxed">
            Join our community of hosts and start earning income by sharing your restorative space with travelers seeking digital detox and mental recovery.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center gap-4 justify-center">
            <button 
              onClick={() => navigate('/signup')}
              className="w-full sm:w-auto bg-emerald-700 hover:bg-emerald-800 text-white px-10 sm:px-12 lg:px-14 py-4 sm:py-5 rounded-lg text-base sm:text-lg font-medium shadow-lg shadow-emerald-200/50 hover:shadow-xl hover:shadow-emerald-300/50 transition-all duration-300"
            >
              Create Host Account
            </button>
            
            <button 
              onClick={() => navigate('/login')}
              className="w-full sm:w-auto bg-white text-gray-700 px-10 sm:px-12 lg:px-14 py-4 sm:py-5 rounded-lg text-base sm:text-lg font-medium border-2 border-gray-200 hover:border-emerald-300 hover:bg-emerald-50/50 transition-all duration-300"
            >
              Sign In
            </button>
          </div>
          
          <p className="mt-6 sm:mt-8 text-sm sm:text-base text-gray-500">
            Free to list • Set your own rates • Secure payments
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;